/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Images } from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";
import { forwardRef, type JSX, type ReactNode, useState } from "react";
import { useFocus, useHover } from "react-aria";

import type { ThemeType } from "../../../../../../global/emotion";
import { radius, spacing } from "../../../../../../global/emotion";
import { stylesOpacity } from "../../../../../../global/emotion/styles/stylesOpacity";
import { stylesOutline } from "../../../../../../global/emotion/styles/stylesOutline";
import { stylesScale } from "../../../../../../global/emotion/styles/stylesScale";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import type { ThumbnailProps } from "../../../../blocks/other/Thumbnail/Thumbnail.emotion";
import { Thumbnail } from "../../../../blocks/other/Thumbnail/Thumbnail.emotion";
import { Text } from "../../../_core/Text/Text.emotion";
import { SubscriptionIcon } from "../../../icons/Icon/GeneralIcons";
import type { IconElement } from "../../../icons/Icon/Icon";
import { InlineIcon } from "../../../icons/Icon/Icon";

const hallowElement: HallowElement = "div";

const determinedElementStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
  paddingRight: spacing.m,
};
const infoContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.xs,
  minWidth: spacing.none,
  textAlign: "left",
  width: spacing.full,
};
const imgStyle: Interpolation = {
  borderRadius: radius.s,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
};
const metadataStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  color: theme.semanticColors.neutralsMedium,
  display: "flex",
  gap: spacing.xs,
});
const rightActionsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};
const textContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
};
const titleContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};

export type ContentSmallProps = WithAsChild<
  Omit<HallowElementProps<typeof hallowElement>, "disabled">
> & {
  activeState?: boolean;
  description?: string;
  focusState?: boolean;
  hoverState?: boolean;
  imgOverlay?: ThumbnailProps["imageOverlay"];
  imgSrc?: Images;
  metadata?: ReactNode;
  metadataInlineIcon?: IconElement;
  rightActions?: JSX.Element[];
  subscription?: boolean;
  supertitle?: string;
  title?: string;
  disabled?: boolean;
  size?: "s" | "xs";
};

export const ContentSmall = forwardRef<any, ContentSmallProps>(
  (
    {
      activeState = true,
      asChild = false,
      children,
      description,
      focusState = true,
      hoverState = true,
      imgOverlay,
      imgSrc,
      metadata,
      metadataInlineIcon,
      rightActions,
      subscription,
      supertitle,
      title,
      disabled,
      size = "s",
      ...props
    },
    ref,
  ) => {
    const [focus, setFocus] = useState(false);

    const { focusProps } = useFocus({
      onFocusChange: (focus) => setFocus(focus),
    });
    const { isHovered: hover, hoverProps } = useHover({});

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <Thumbnail
          title={title}
          key="img"
          imageSrc={imgSrc}
          imageOverlay={imgOverlay}
          size={size}
          css={[
            imgStyle,
            stylesOutline.base,
            focus && focusState ? stylesOutline.focusControlled : null,
            hover && hoverState ? stylesOutline.hoverControlled : null,
          ]}
        />,
        <div key="infoContainer" css={infoContainerStyle}>
          <div css={textContainerStyle}>
            {supertitle ? (
              <Text type="detail" size="l" overflow="ellipsis">
                {supertitle}
              </Text>
            ) : null}
            <div css={titleContainerStyle}>
              {subscription && <InlineIcon icon={<SubscriptionIcon.Small />} />}
              <Text
                overflow="ellipsis"
                size={size === "xs" ? "m" : "l"}
                type="title"
              >
                {title}
              </Text>
            </div>
            {description && (
              <Text overflow="ellipsis" size="s" type="body">
                {description}
              </Text>
            )}
          </div>
          {metadata && (
            <Text
              overflow="ellipsis"
              size="l"
              type="detail"
              css={metadataStyle}
            >
              {metadataInlineIcon && <InlineIcon icon={metadataInlineIcon} />}
              {metadata}
            </Text>
          )}
        </div>,
        rightActions?.length > 0 ? (
          <div key="rightActions" css={rightActionsStyle}>
            {rightActions}
          </div>
        ) : null,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        data-testid={HallowDataTestId.ContentSmall}
        ref={ref}
        {...(disabled ? {} : focusProps)}
        {...(disabled ? {} : hoverProps)}
        css={[
          determinedElementStyle,
          !disabled && activeState ? stylesOpacity.active : null,
          !disabled && activeState ? stylesOpacity.base : null,
          !disabled && activeState && hoverState ? stylesScale.base : null,
          !disabled && activeState && hoverState ? stylesScale.l : null,
        ]}
        {...props}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

ContentSmall.displayName = "ContentSmall";
